import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DOMPurify from "dompurify";
import {
    FaBookOpen, FaExternalLinkAlt, FaPen, FaPlus, FaSearch, FaThumbtack, FaStar, FaEyeSlash, FaSlidersH,
} from "react-icons/fa";
import { AppModal } from "../../../../../_CORE/APP/Templates/_UI/Modal/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { FieldDate, FieldInput, FieldSelect } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { Badge, EmptyState } from "../../../../../_CORE/APP/Templates/_UI/Kit/_index";
import { AdvancedFilterPanel, onDateMatches } from "../../../../_UI/AdvancedFilterPanel";
import { NOTEBOOK_STATUSES, NOTEBOOK_TYPES } from "./NotebookEntryFormModal";
import "../../../../../_CORE/APP/Templates/_UI/ReadOnly/_Styles/ReadOnly.css";

const labelOf = (options, value) => options.find((item) => item.value === value)?.label || value || "—";
const plain = (html = "") => DOMPurify.sanitize(String(html), { ALLOWED_TAGS: [] }).replace(/&nbsp;/g, " ").trim();
const valueOrDash = (value) => String(value ?? "").trim() || "—";
const compactPreview = (entry = {}) => {
    const raw = String(entry.summary || plain(entry.content) || "")
        .replace(/[\u200B-\u200D\uFEFF]/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    if (!raw) return "Sin contenido todavía.";
    // Evita previews que visualmente terminan siendo sólo separadores/puntos.
    const readable = raw.replace(/(?:[._·•\-]\s*){4,}/g, " ").replace(/\s+/g, " ").trim();
    return (readable || raw).slice(0, 150);
};
const emptyWorkspaceAdvanced = () => ({ type: "ALL", status: "ALL", favorite: "ALL", author: "", dateField: "UPDATED", dateRange: [null, null], dateExact: null });
const formatDate = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString("es-AR", { dateStyle: "medium", timeStyle: "short" });
};

export const NotebookCollectionWorkspaceModal = ({
    isOpen,
    collection = null,
    entries = [],
    focusEntryId = null,
    onFocusEntryHandled,
    onClose,
    onNewEntry,
    onEditEntry,
    onEditCollection,
    isLoading = false,
}) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [search, setSearch] = useState("");
    const [selectedId, setSelectedId] = useState(null);
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [advanced, setAdvanced] = useState(() => emptyWorkspaceAdvanced());
    const advancedCount = [
        advanced.type !== "ALL", advanced.status !== "ALL", advanced.favorite !== "ALL",
        String(advanced.author || "").trim(), advanced.dateRange?.[0] || advanced.dateRange?.[1], advanced.dateExact,
    ].filter(Boolean).length;

    const rows = useMemo(() => (Array.isArray(entries) ? entries : [])
        .filter((entry) => String(entry.collectionId || "") === String(collection?.id || ""))
        .sort((a, b) => (Number(!!b.isPinned) - Number(!!a.isPinned))
            || (Number(!!b.isFavorite) - Number(!!a.isFavorite))
            || String(b.updatedAt || "").localeCompare(String(a.updatedAt || ""))),
    [entries, collection?.id]);

    const visible = useMemo(() => {
        const q = String(search || "").trim().toLowerCase();
        return rows.filter((entry) => {
            if (q && ![
                entry.title, entry.subtitle, entry.summary, entry.author, entry.type, entry.status,
                entry.genre, entry.mood, entry.language, entry.source, entry.bookTitle, entry.sectionName,
                plain(entry.content), ...(entry.tags || []),
                ...(entry.referenceLinks || []).flatMap((row) => [row?.label, row?.url]),
                ...(entry.customFields || []).flatMap((row) => [row?.label, row?.value]),
            ].some((value) => String(value || "").toLowerCase().includes(q))) return false;
            if (advanced.type !== "ALL" && String(entry.type || "") !== String(advanced.type)) return false;
            if (advanced.status !== "ALL" && String(entry.status || "") !== String(advanced.status)) return false;
            if (advanced.favorite !== "ALL" && (!!entry.isFavorite !== (advanced.favorite === "YES"))) return false;
            if (String(advanced.author || "").trim() && !String(entry.author || "").toLowerCase().includes(String(advanced.author).trim().toLowerCase())) return false;
            const dateValue = advanced.dateField === "CREATED" ? entry.createdAt : entry.updatedAt;
            return onDateMatches(dateValue, advanced.dateRange, advanced.dateExact);
        });
    }, [rows, search, advanced]);

    const selected = visible.find((entry) => String(entry.id) === String(selectedId)) || null;

    useEffect(() => {
        if (!isOpen) return;
        setSearch("");
        setAdvanced(emptyWorkspaceAdvanced());
        setAdvancedOpen(searchParams.get("entryAdvanced") === "1");
        // La URL es la fuente de verdad: si recargamos, conservamos el registro elegido.
        // Si no hay `entry`, la colección abre vacía y el usuario decide qué ver.
        setSelectedId(searchParams.get("entry") || null);
    }, [isOpen, collection?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!isOpen || isLoading) return;
        const routed = searchParams.get("entry");
        if (!routed) return;
        if (rows.some((entry) => String(entry.id) === String(routed))) {
            setSelectedId(String(routed));
        }
        // No borres el id de la URL mientras hidratan los datos.
        // Si la API tarda un render, el registro sigue siendo recuperable al recargar.
    }, [isOpen, isLoading, searchParams, rows, setSearchParams]);

    useEffect(() => {
        if (!isOpen || !focusEntryId) return;
        if (!rows.some((entry) => String(entry.id) === String(focusEntryId))) return;
        setSelectedId(focusEntryId);
        onFocusEntryHandled?.(focusEntryId);
    }, [focusEntryId, rows, isOpen, onFocusEntryHandled]);

    const updateRouteState = (patch = {}) => {
        const next = new URLSearchParams(window.location.search);
        Object.entries(patch).forEach(([key, value]) => {
            if (value === null || value === undefined || value === "") next.delete(key);
            else next.set(key, String(value));
        });
        setSearchParams(next, { replace: true });
    };

    const selectEntry = (id) => {
        setSelectedId(id);
        updateRouteState({ entry: id, preview: null });
    };

    const hidePreview = () => {
        // Ocultar significa cerrar el registro actual, no esconder la lista de la colección.
        setSelectedId(null);
        updateRouteState({ entry: null, preview: null });
    };

    const toggleAdvanced = () => {
        const next = !advancedOpen;
        setAdvancedOpen(next);
        updateRouteState({ entryAdvanced: next ? "1" : null });
    };

    if (!collection) return null;

    const links = (selected?.referenceLinks || []).filter((link) => String(link?.url || "").trim());
    const fields = Array.isArray(selected?.customFields) ? selected.customFields : [];
    const compositionData = selected ? [
        ["Género", selected.genre], ["Clima", selected.mood], ["Tonalidad", selected.musicalKey],
        ["BPM", selected.bpm], ["Compás", selected.timeSignature], ["Afinación", selected.tuning],
        ["Capo", selected.capo], ["Versión", selected.versionLabel],
    ].filter(([, value]) => String(value ?? "").trim()) : [];

    return <AppModal
        isOpen={isOpen}
        onClose={onClose}
        size="full"
        className="LStudio__notebook_collection_workspace_modal"
        title={collection.name || "Colección"}
        description={collection.isVirtual ? null : (collection.description || null)}
    >
        <div className="LStudio__notebook_collection_workspace">
            <aside className="LStudio__notebook_collection_workspace_sidebar">
                <div className="LStudio__notebook_collection_workspace_actions">
                    <AppButton size="sm" icon={<FaPlus />} onClick={() => onNewEntry?.(collection)}>Nuevo registro</AppButton>
                    {!collection.isVirtual && <AppButton size="sm" variant="outline" tone="neutral" icon={<FaPen />} onClick={() => onEditCollection?.(collection)}>Editar colección</AppButton>}
                </div>

                <div className="LStudio__notebook_collection_workspace_filterbar">
                    <label className="LStudio__notebook_collection_workspace_search">
                        <FaSearch />
                        <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar registros..." />
                    </label>
                    <button type="button" className="LStudio__notebook_collection_workspace_filter_toggle" data-open={advancedOpen ? "true" : "false"}
                        onClick={toggleAdvanced} title="Filtros avanzados" aria-label="Filtros avanzados"><FaSlidersH /></button>
                </div>

                <AdvancedFilterPanel isOpen={advancedOpen} activeCount={advancedCount} className="LStudio__notebook_collection_workspace_advanced" onClear={() => setAdvanced(emptyWorkspaceAdvanced())}>
                    <FieldSelect id="notebook_ws_type" label="Tipo" value={advanced.type}
                        onChange={(event) => setAdvanced((current) => ({ ...current, type: event.target.value }))} options={[{ value: "ALL", label: "Todos" }, ...NOTEBOOK_TYPES]} />
                    <FieldSelect id="notebook_ws_status" label="Estado" value={advanced.status}
                        onChange={(event) => setAdvanced((current) => ({ ...current, status: event.target.value }))} options={[{ value: "ALL", label: "Todos" }, ...NOTEBOOK_STATUSES]} />
                    <FieldSelect id="notebook_ws_favorite" label="Destacado" value={advanced.favorite}
                        onChange={(event) => setAdvanced((current) => ({ ...current, favorite: event.target.value }))} options={[
                            { value: "ALL", label: "Todos" }, { value: "YES", label: "Destacados" }, { value: "NO", label: "No destacados" },
                        ]} />
                    <FieldInput id="notebook_ws_author" label="Autor / responsable" value={advanced.author}
                        onChange={(event) => setAdvanced((current) => ({ ...current, author: event.target.value }))} />
                    <FieldSelect id="notebook_ws_date" label="Fecha" value={advanced.dateField}
                        onChange={(event) => setAdvanced((current) => ({ ...current, dateField: event.target.value }))} options={[
                            { value: "UPDATED", label: "Actualización" }, { value: "CREATED", label: "Creación" },
                        ]} />
                    <FieldDate id="notebook_ws_from" label="Desde" value={advanced.dateRange?.[0] || null}
                        onChange={(date) => setAdvanced((current) => ({ ...current, dateRange: [date || null, current.dateRange?.[1] || null], dateExact: null }))} isClearable />
                    <FieldDate id="notebook_ws_to" label="Hasta" value={advanced.dateRange?.[1] || null}
                        onChange={(date) => setAdvanced((current) => ({ ...current, dateRange: [current.dateRange?.[0] || null, date || null], dateExact: null }))} isClearable />
                    <FieldDate id="notebook_ws_exact" label="Fecha exacta" value={advanced.dateExact}
                        onChange={(date) => setAdvanced((current) => ({ ...current, dateExact: date || null, dateRange: date ? [null, null] : current.dateRange }))} isClearable />
                </AdvancedFilterPanel>

                <div className="LStudio__notebook_collection_workspace_count">{visible.length} {visible.length === 1 ? "registro" : "registros"}</div>

                <div className="LStudio__notebook_collection_workspace_list">
                    {visible.map((entry) => <button
                        key={entry.id}
                        type="button"
                        data-active={String(entry.id) === String(selectedId)}
                        onClick={() => selectEntry(entry.id)}
                    >
                        <div className="LStudio__notebook_collection_workspace_item_title">
                            <strong>{entry.title || "Sin título"}</strong>
                            <span>{entry.isPinned && <FaThumbtack title="Fijado" />}{entry.isFavorite && <FaStar title="Destacado" />}</span>
                        </div>
                        <small>{labelOf(NOTEBOOK_TYPES, entry.type)} · {labelOf(NOTEBOOK_STATUSES, entry.status)}</small>
                        <p>{compactPreview(entry)}</p>
                    </button>)}
                    {!visible.length && <div className="LStudio__notebook_collection_workspace_empty_list">No hay registros que coincidan con la búsqueda.</div>}
                </div>
            </aside>

            {selected ? <section className="LStudio__notebook_collection_workspace_reader">
                <main className="LStudio__notebook_collection_workspace_reader_main">
                    <div className="LStudio__notebook_collection_workspace_reader_toolbar">
                        <div className="LStudio__notebook_collection_workspace_badges">
                            <Badge tone="neutral" size="sm">{labelOf(NOTEBOOK_TYPES, selected.type)}</Badge>
                            <Badge tone="neutral" size="sm">{labelOf(NOTEBOOK_STATUSES, selected.status)}</Badge>
                            {selected.isPinned && <Badge tone="accent" size="sm"><FaThumbtack /> Fijado</Badge>}
                            {selected.isFavorite && <Badge tone="accent" size="sm"><FaStar /> Destacado</Badge>}
                        </div>
                        <div className="LStudio__notebook_collection_workspace_reader_actions">
                            <AppButton variant="ghost" tone="neutral" icon={<FaEyeSlash />} onClick={hidePreview}>Ocultar</AppButton>
                            <AppButton icon={<FaPen />} onClick={() => onEditEntry?.(selected, collection)}>Escribir / editar</AppButton>
                        </div>
                    </div>

                    <article className="LStudio__readonly_document LStudio__notebook_workspace_document">
                        <div className="LStudio__readonly_document_title">
                            <span><FaBookOpen /></span>
                            <div>
                                <small>{collection.isVirtual ? "Sin colección" : collection.name}</small>
                                <h1>{selected.title || "Sin título"}</h1>
                                {selected.subtitle && <p className="LStudio__notebook_reader_subtitle">{selected.subtitle}</p>}
                            </div>
                        </div>

                        {selected.summary && <section className="LStudio__readonly_section LStudio__notebook_reader_summary_full">
                            <strong>Resumen / sinopsis</strong>
                            <p>{selected.summary}</p>
                        </section>}

                        {(selected.tags || []).length > 0 && <div className="LStudio__notebook_reader_tags_full">
                            {(selected.tags || []).map((tag) => <Badge key={tag} tone="neutral" size="sm">{tag}</Badge>)}
                        </div>}

                        <div className="LStudio__notebook_reader_content_full LStudio__notebook_workspace_content"
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selected.content || "<p>Sin contenido todavía.</p>") }} />
                    </article>
                </main>

                <aside className="LStudio__notebook_collection_workspace_reader_meta">
                    <section className="LStudio__readonly_meta_section">
                        <strong>Organización</strong>
                        <h3>{collection.isVirtual ? "Sin colección" : collection.name}</h3>
                        <div className="LStudio__notebook_reader_meta_badges">
                            <Badge tone="neutral" size="sm">{labelOf(NOTEBOOK_TYPES, selected.type)}</Badge>
                            <Badge tone="neutral" size="sm">{labelOf(NOTEBOOK_STATUSES, selected.status)}</Badge>
                        </div>
                    </section>

                    <section className="LStudio__readonly_meta_section">
                        <strong>Lectura</strong>
                        <div className="LStudio__notebook_reader_stats_grid">
                            <div><span>Palabras</span><b>{selected.wordCount || 0}</b></div>
                            <div><span>Caracteres</span><b>{selected.characterCount || 0}</b></div>
                            <div><span>Tiempo</span><b>{selected.readingMinutes || 0} min</b></div>
                            <div><span>Meta</span><b>{valueOrDash(selected.wordGoal)}</b></div>
                        </div>
                    </section>

                    {(selected.author || selected.language || selected.source) && <section className="LStudio__readonly_meta_section">
                        <strong>Datos</strong>
                        <div className="LStudio__notebook_reader_side_rows">
                            {selected.author && <div><span>Autor / responsable</span><b>{selected.author}</b></div>}
                            {selected.language && <div><span>Idioma</span><b>{selected.language}</b></div>}
                            {selected.source && <div><span>Fuente / contexto</span><b>{selected.source}</b></div>}
                        </div>
                    </section>}

                    {compositionData.length > 0 && <section className="LStudio__readonly_meta_section">
                        <strong>Composición</strong>
                        <div className="LStudio__notebook_reader_side_rows">
                            {compositionData.map(([label, value]) => <div key={label}><span>{label}</span><b>{value}</b></div>)}
                        </div>
                    </section>}

                    {(selected.bookTitle || selected.chapterNumber || selected.sectionName) && <section className="LStudio__readonly_meta_section">
                        <strong>Libro / capítulo</strong>
                        <div className="LStudio__notebook_reader_side_rows">
                            {selected.bookTitle && <div><span>Obra</span><b>{selected.bookTitle}</b></div>}
                            {selected.chapterNumber && <div><span>Capítulo</span><b>{selected.chapterNumber}</b></div>}
                            {selected.sectionName && <div><span>Sección</span><b>{selected.sectionName}</b></div>}
                        </div>
                    </section>}

                    {links.length > 0 && <section className="LStudio__readonly_meta_section">
                        <strong>Referencias</strong>
                        <div className="LStudio__notebook_reader_side_links">
                            {links.map((link, index) => <a key={`${link.url}-${index}`} href={link.url} target="_blank" rel="noreferrer">
                                <FaExternalLinkAlt /><span>{link.label || link.url}</span>
                            </a>)}
                        </div>
                    </section>}

                    {fields.length > 0 && <section className="LStudio__readonly_meta_section">
                        <strong>Campos propios</strong>
                        <div className="LStudio__notebook_reader_side_rows">
                            {fields.map((field, index) => <div key={`${field.label || "field"}-${index}`}>
                                <span>{field.label || `Campo ${index + 1}`}</span><b>{valueOrDash(field.value)}</b>
                            </div>)}
                        </div>
                    </section>}

                    <section className="LStudio__readonly_meta_section">
                        <strong>Registro</strong>
                        <div className="LStudio__notebook_reader_side_rows">
                            <div><span>Creado</span><b>{formatDate(selected.createdAt)}</b></div>
                            <div><span>Actualizado</span><b>{formatDate(selected.updatedAt)}</b></div>
                        </div>
                    </section>
                </aside>
            </section> : <main className="LStudio__notebook_collection_workspace_preview">
                <EmptyState
                    icon={<FaBookOpen />}
                    title={rows.length && !visible.length ? "No hay coincidencias" : rows.length ? "Elegí un registro" : (collection.isVirtual ? "No hay registros sin colección" : "Esta colección todavía está vacía")}
                    description={rows.length && !visible.length ? "Cambiá la búsqueda del panel izquierdo para volver a ver registros." : rows.length ? "Seleccioná un contenido de la izquierda para visualizarlo acá." : (collection.isVirtual ? "Los registros que crees sin asignar una colección van a aparecer acá." : "Creá el primer registro y va a quedar organizado dentro de esta colección.")}
                    action={!rows.length ? <AppButton icon={<FaPlus />} onClick={() => onNewEntry?.(collection)}>Nuevo registro</AppButton> : null}
                />
            </main>}
        </div>
    </AppModal>;
};
